<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Modal, Input, Select, SelectOption, Button } from 'ant-design-vue'
import { AlertCircle } from 'lucide-vue-next'
import { InjectionService } from '@/shared/services/InjectionService'
import { FolderApiService, type IFolderApiService } from '../services/FolderApiService'
import type { Folder } from '../models/Folder'
import type { CreateFolderRequest } from '../models/FolderRequests'

const PREDEFINED_COLORS = [
  { value: 'blue', label: 'Blue' },
  { value: 'green', label: 'Green' },
  { value: 'red', label: 'Red' },
  { value: 'orange', label: 'Orange' },
  { value: 'purple', label: 'Purple' },
  { value: 'teal', label: 'Teal' },
  { value: 'pink', label: 'Pink' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'indigo', label: 'Indigo' },
  { value: 'gray', label: 'Gray' },
]

const props = defineProps<{
  open: boolean
  folders: Folder[]
  tenantId: string
  userId: string
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'folder-created': [folder: Folder]
}>()

const folderApiService: IFolderApiService =
  InjectionService.injectStrict(FolderApiService.injectableKey)

// --- State ---
const folderName = ref('')
const parentFolderId = ref<string | null>(null)
const selectedColor = ref<string | null>(null)
const submitting = ref(false)
const errorMessage = ref<string | null>(null)

// --- Computed ---
const trimmedName = computed(() => folderName.value.trim())

const nameValidationError = computed(() => {
  if (folderName.value.length === 0) return null
  if (trimmedName.value.length === 0) return 'Folder name must contain at least one non-whitespace character.'
  if (folderName.value.length > 100) return 'Folder name must be 100 characters or fewer.'
  return null
})

const canSubmit = computed(() => {
  return (
    trimmedName.value.length >= 1 &&
    trimmedName.value.length <= 100 &&
    !nameValidationError.value &&
    !submitting.value
  )
})

// --- Methods ---
function resetState() {
  folderName.value = ''
  parentFolderId.value = null
  selectedColor.value = null
  submitting.value = false
  errorMessage.value = null
}

async function handleSubmit() {
  if (!canSubmit.value) return

  submitting.value = true
  errorMessage.value = null

  try {
    const request: CreateFolderRequest = {
      name: trimmedName.value,
    }

    if (parentFolderId.value) {
      request.parentFolderId = parentFolderId.value
    }

    if (selectedColor.value) {
      request.color = selectedColor.value
    }

    const response = await folderApiService.createFolder(props.tenantId, props.userId, request)
    const newFolder = response.data
    emit('folder-created', newFolder)
    handleClose()
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'An unexpected error occurred. Please try again.'
    errorMessage.value = message
  } finally {
    submitting.value = false
  }
}

function handleClose() {
  resetState()
  emit('update:open', false)
}

// Reset state when dialog opens
watch(
  () => props.open,
  (newVal) => {
    if (newVal) {
      resetState()
    }
  }
)
</script>

<template>
  <Modal
    :open="open"
    title="Create Folder"
    :footer="null"
    :maskClosable="false"
    width="460px"
    @cancel="handleClose"
    data-testid="folder-create-dialog"
  >
    <div class="space-y-4">
      <!-- Folder Name -->
      <div>
        <label class="mb-1.5 block text-sm font-medium">Folder Name</label>
        <Input
          v-model:value="folderName"
          placeholder="Enter folder name"
          :maxlength="100"
          :disabled="submitting"
          :status="nameValidationError ? 'error' : undefined"
          data-testid="folder-name-input"
          @pressEnter="handleSubmit"
        />
        <p v-if="nameValidationError" class="mt-1 text-xs text-red-500" data-testid="name-validation-error">
          {{ nameValidationError }}
        </p>
        <p v-else class="mt-1 text-xs text-muted-foreground">
          {{ folderName.length }}/100 characters
        </p>
      </div>

      <!-- Parent Folder -->
      <div>
        <label class="mb-1.5 block text-sm font-medium">Parent Folder (optional)</label>
        <Select
          v-model:value="parentFolderId"
          placeholder="None (top-level)"
          allowClear
          :disabled="submitting"
          class="w-full"
          data-testid="parent-folder-select"
        >
          <SelectOption
            v-for="folder in folders"
            :key="folder.id"
            :value="folder.id"
          >
            {{ folder.name }}
          </SelectOption>
        </Select>
      </div>

      <!-- Color Picker -->
      <div>
        <label class="mb-1.5 block text-sm font-medium">Color (optional)</label>
        <Select
          v-model:value="selectedColor"
          placeholder="No color"
          allowClear
          :disabled="submitting"
          class="w-full"
          data-testid="color-select"
        >
          <SelectOption
            v-for="color in PREDEFINED_COLORS"
            :key="color.value"
            :value="color.value"
          >
            <div class="flex items-center gap-2">
              <span
                class="inline-block h-3 w-3 rounded-full"
                :style="{ backgroundColor: color.value }"
              />
              {{ color.label }}
            </div>
          </SelectOption>
        </Select>
      </div>

      <!-- Error Message -->
      <div
        v-if="errorMessage"
        class="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950"
        data-testid="submit-error"
      >
        <div class="flex items-start gap-2">
          <AlertCircle class="mt-0.5 size-4 flex-shrink-0 text-red-500" />
          <p class="text-sm text-red-700 dark:text-red-300">{{ errorMessage }}</p>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-2 border-t pt-4">
        <Button
          @click="handleClose"
          :disabled="submitting"
          data-testid="cancel-btn"
        >
          Cancel
        </Button>
        <Button
          type="primary"
          :disabled="!canSubmit"
          :loading="submitting"
          @click="handleSubmit"
          data-testid="create-btn"
        >
          Create
        </Button>
      </div>
    </div>
  </Modal>
</template>
